/**
 * Platform abstraction: web, Capacitor (iOS/Android) and the Electron desktop shell. Native
 * purchases live in ./billing; native plugins are loaded lazily so the web build never pays for them.
 */
export type PlatformId = 'web' | 'android' | 'ios' | 'desktop';

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

function capacitor(): CapacitorLike | null {
  return ((window as unknown as { Capacitor?: CapacitorLike }).Capacitor ?? null) as CapacitorLike | null;
}

function detect(): PlatformId {
  const cap = capacitor();
  if (cap?.isNativePlatform?.()) return cap.getPlatform?.() === 'ios' ? 'ios' : 'android';
  if (/Electron\//.test(navigator.userAgent)) return 'desktop';
  return 'web';
}

export const platform: PlatformId = detect();
export const isNativeApp = platform === 'android' || platform === 'ios';

/**
 * Web game portal this build is made for (`VITE_PORTAL=crazygames`, tools/build-crazygames.mjs), else null. A portal build is
 * still a web build (`platform` stays 'web'), but the portal owns the player's account, ads, links and fullscreen: no
 * onboarding or email login, no web checkout, no store or website links, no custom fullscreen button.
 */
export const portal: 'crazygames' | null = import.meta.env.VITE_PORTAL === 'crazygames' ? 'crazygames' : null;

/** Name of the store that handles purchases on this platform. */
export function storeName(): string {
  if (platform === 'ios') return 'the App Store';
  if (platform === 'android') return 'Google Play';
  return 'our secure checkout';
}

/**
 * Opens an external page (checkout, policies): an in-app browser sheet on phones, the system
 * browser on desktop, and the same tab on the web when `sameTab` is set so checkout can return.
 */
export function openExternal(url: string, sameTab = false): void {
  if (isNativeApp) {
    import('@capacitor/browser')
      .then(({ Browser }) => Browser.open({ url }))
      .catch(() => window.open(url, '_blank'));
    return;
  }
  if (sameTab && platform === 'web') location.href = url;
  else window.open(url, '_blank', 'noopener');
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Uses the native share sheet when available, otherwise copies the text. */
export async function shareText(title: string, text: string): Promise<'shared' | 'copied' | 'failed'> {
  const nav = navigator as Navigator & { share?: (d: { title?: string; text?: string }) => Promise<void> };
  if (nav.share && (isNativeApp || matchMedia('(pointer: coarse)').matches)) {
    try {
      await nav.share({ title, text });
      return 'shared';
    } catch {
      /* dismissed — fall back to copy */
    }
  }
  return (await copyText(text)) ? 'copied' : 'failed';
}

let hapticsLoad: Promise<typeof import('@capacitor/haptics') | null> | null = null;

/** Short tap on supported touch devices (Taptic Engine on iOS, where navigator.vibrate is missing). */
export function haptic(ms = 12): void {
  if (isNativeApp) {
    hapticsLoad ??= import('@capacitor/haptics').catch(() => null);
    void hapticsLoad.then((m) => m?.Haptics.impact({ style: ms > 20 ? m.ImpactStyle.Medium : m.ImpactStyle.Light }).catch(() => {}));
    return;
  }
  try {
    navigator.vibrate?.(ms);
  } catch {
    /* unsupported */
  }
}
